/**
 * Maps an onboarding focus-area card to a landing path, and optionally a guide.
 * Path keys stay aligned with app-web-onboarding `painpoint-landing.util.ts`.
 * Guide keys live only here.
 */

export interface PainpointLanding {
  readonly path: string;
  readonly guideKey?: string;
}

const PAINPOINT_LANDINGS: Readonly<Record<string, PainpointLanding>> = {
  accountability: { path: '/activity-summary', guideKey: 'guide-accountability' },
  accountabilityfocus: { path: '/activity-summary', guideKey: 'guide-accountability-focus' },
  productivity: { path: '/dashboard', guideKey: 'guide-productivity-perform' },
  productivityperformance: { path: '/dashboard', guideKey: 'guide-productivity-perform' },
  timetrackingattendance: { path: '/hours-tracked', guideKey: 'guide-time-attendance' },
  timeattendance: { path: '/hours-tracked', guideKey: 'guide-time-attendance' },
  payroll: { path: '/projects-report', guideKey: 'guide-payroll-billing' },
  payrollbilling: { path: '/projects-report', guideKey: 'guide-payroll-billing' },
  aiproductivityinsights: { path: '/benchmarks', guideKey: 'guide-ai-productivity-insi-740' },
  softwarecost: { path: '/software-cost-insights', guideKey: 'guide-software-cost-utiliz' },
  softwarecostutilization: { path: '/software-cost-insights', guideKey: 'guide-software-cost-utiliz' },
};

export function normalizePainpoint(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Pathname only. Drops query strings and Angular matrix params. */
export function routePath(url: string): string {
  const path = url.split('?')[0].split(';')[0];
  return path.length > 1 ? path.replace(/\/$/, '') : path;
}

export function resolvePainpointLanding(painpoint: string | null | undefined): PainpointLanding | null {
  if (!painpoint?.trim()) {
    return null;
  }

  return PAINPOINT_LANDINGS[normalizePainpoint(painpoint)] ?? null;
}

export interface PainpointGuideVisit {
  painpoint: string | null | undefined;
  url: string;
  trackingMode: string | undefined;
  accessLevel: string | null | undefined;
}

/** Guide to open for this visit. Silent companies and non-owners are excluded. */
export function painpointGuideKeyForVisit(visit: PainpointGuideVisit): string | null {
  if (visit.trackingMode === 'silent' || visit.accessLevel !== 'owner') {
    return null;
  }

  const landing = resolvePainpointLanding(visit.painpoint);
  if (!landing?.guideKey || routePath(visit.url) !== landing.path) {
    return null;
  }

  return landing.guideKey;
}
